import CoverHero from "@/assets/img/astro-map/cover-hero.png"
import IcClock from "@/assets/img/astro-map/ic-clock.svg"
import IcCompass from "@/assets/img/astro-map/ic-compass.svg"
import IcLines from "@/assets/img/astro-map/ic-lines.svg"
import { Button } from "@/components/ui/button"
import { AppRoutes } from "@/shared/lib/routes"
import { LegalLink } from "@/shared/components/legal-link"
import { usePreloadQuizImages } from "../../hooks/use-preload-quiz-images"
import type { StepComponentProps } from "./step-contract"

/** The three stat tiles on the cover (Figma Step0): clock / lines / compass. */
const STATS = [
  { icon: IcClock, label: ["3-minute", "quiz"] },
  { icon: IcLines, label: ["Map your", "lines"] },
  { icon: IcCompass, label: ["Personalized guide"] },
]

/**
 * Astro Map cover (Figma LPR_Quiz_Map_Step0): a full-width astrocartography globe
 * hero, the value-prop title/subtitle, a dark-surface card with three stat tiles
 * + the CTA, then the policy links. Copy comes from config; the CTA calls
 * `onAdvance`. Mirrors the Soulmate cover structure with the globe hero and the
 * design's custom stat icons.
 */
export function AstroCoverStep({ step, onAdvance }: StepComponentProps) {
  // Warm the later steps' images while the user is on this first screen, so the
  // quiz doesn't flash-load each image on step entry.
  usePreloadQuizImages("astro-map")

  return (
    <div className="flex flex-1 flex-col justify-end bg-dark-base text-purple-light">
      <img src={CoverHero} alt="" className="h-[333px] w-full object-cover" />

      <div className="flex flex-col gap-4 px-4 pb-4">
        <div className="space-y-2 text-center">
          <h1 className="font-display text-2xl font-medium text-purple-light">{step.title}</h1>
          {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 rounded-[24px] bg-dark-surface px-1 pt-1 pb-2">
            <div className="flex gap-1">
              {STATS.map((stat) => (
                <div
                  key={stat.label.join(" ")}
                  className="flex flex-1 flex-col items-center justify-end gap-2 rounded-[20px] bg-purple-light/10 px-2 py-3"
                >
                  <img src={stat.icon} alt="" className="size-6" />
                  <span className="text-center text-sm leading-tight text-purple-light">
                    {stat.label.map((line, i) => (
                      <span key={line} className="block">
                        {line}
                        {i < stat.label.length - 1 ? null : null}
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
            <div className="px-1">
              <Button variant="gradient" size="xl" className="w-full" onClick={onAdvance}>
                {step.cta ?? "Let's begin"}
              </Button>
            </div>
          </div>

          <div className="flex justify-between px-1 text-center text-xs text-purple-light/40">
            <span className="flex-1">
              <LegalLink to={AppRoutes.PRIVACY}>Privacy Policy</LegalLink>
            </span>
            <span className="flex-1">
              <LegalLink to={AppRoutes.TERMS}>Terms of use</LegalLink>
            </span>
            <span className="flex-1">
              <LegalLink to={AppRoutes.COOKIES}>Cookie Policy</LegalLink>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
